import { trackEvent } from '../utils/analytics';
import {
  NOTIFICATION_EVENTS,
  createEventProperties,
  type NotificationEventProperties,
} from '../utils/eventTracking';

export const useNotificationTracking = (): {
  trackNotificationClick: (
    notificationType: string,
    notificationId: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationView: (
    notificationType: string,
    notificationId: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackWatchlistAdd: (
    certificationIds: number[],
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;
  trackWatchlistRemove: (
    certificationIds: number[],
    properties?: Parameters<typeof createEventProperties>[0]
  ) => void;

  // Notification page tracking functions
  trackNotificationPageView: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationMarkRead: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationMarkUnread: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationSelect: (
    notificationId: number,
    selected: boolean,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkAction: (
    actionType: 'mark_read' | 'mark_unread',
    selectedCount: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationFilterChange: (
    filterType: string,
    filterValue: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationPagination: (
    page: number,
    totalPages: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationRefresh: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationHelpClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationTourClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationSettingsClick: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationAlertDismiss: (
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkDetailsOpen: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationBulkDetailsClose: (
    notificationId: number,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationCardExpand: (
    cardName: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
  trackNotificationCardCollapse: (
    cardName: string,
    properties?: Partial<NotificationEventProperties>
  ) => void;
} => {
  return {
    // Notification tracking (keeping existing functions for compatibility)
    trackNotificationClick: (
      notificationType: string,
      notificationId: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CLICK, {
        notification_type: notificationType,
        notification_id: Number(notificationId),
        action: 'notification_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationView: (
      notificationType: string,
      notificationId: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_VIEW, {
        notification_type: notificationType,
        notification_id: Number(notificationId),
        action_type: 'notification_view',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    // Watchlist tracking (keeping existing functions for compatibility)
    trackWatchlistAdd: (
      certificationIds: number[],
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackEvent(NOTIFICATION_EVENTS.WATCHLIST_ADD, {
        source: 'watch_toggle',
        action: 'add_to_watchlist',
        certification_ids: certificationIds,
        ...properties,
      }),

    trackWatchlistRemove: (
      certificationIds: number[],
      properties: Parameters<typeof createEventProperties>[0] = {}
    ) =>
      trackEvent(NOTIFICATION_EVENTS.WATCHLIST_REMOVE, {
        source: 'watch_toggle',
        action: 'remove_from_watchlist',
        certification_ids: certificationIds,
        ...properties,
      }),

    // New notification page tracking functions
    trackNotificationPageView: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_VIEW, {
        action_type: 'notification_view',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationMarkRead: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_MARK_READ, {
        notification_id: notificationId,
        action: 'mark_read',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationMarkUnread: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_MARK_UNREAD, {
        notification_id: notificationId,
        action: 'mark_unread',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationSelect: (
      notificationId: number,
      selected: boolean,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_SELECT, {
        notification_id: notificationId,
        action_type: 'select',
        selected,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkAction: (
      actionType: 'mark_read' | 'mark_unread',
      selectedCount: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_ACTION, {
        bulk_action_type: actionType,
        selected_count: selectedCount,
        action_type: 'bulk_action',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationFilterChange: (
      filterType: string,
      filterValue: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_FILTER_CHANGE, {
        action: 'filter_change',
        filter_type: filterType,
        filter_value: filterValue,
        card_name: 'filters',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationPagination: (
      page: number,
      totalPages: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_PAGINATION, {
        current_page: page,
        total_pages: totalPages,
        action_type: 'pagination',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationRefresh: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_REFRESH, {
        action_type: 'refresh',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationHelpClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_HELP_CLICK, {
        action_type: 'help_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationTourClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_TOUR_CLICK, {
        action_type: 'tour_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationSettingsClick: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_SETTINGS_CLICK, {
        action_type: 'settings_click',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationAlertDismiss: (
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_ALERT_DISMISS, {
        action_type: 'alert_dismiss',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkDetailsOpen: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_DETAILS_OPEN, {
        notification_id: notificationId,
        action_type: 'bulk_details_open',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationBulkDetailsClose: (
      notificationId: number,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_BULK_DETAILS_CLOSE, {
        notification_id: notificationId,
        action_type: 'bulk_details_close',
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationCardExpand: (
      cardName: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CARD_INTERACTION, {
        card_name: cardName,
        action: 'expand',
        card_expanded: true,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },

    trackNotificationCardCollapse: (
      cardName: string,
      properties: Partial<NotificationEventProperties> = {}
    ) => {
      trackEvent(NOTIFICATION_EVENTS.NOTIFICATION_CARD_INTERACTION, {
        card_name: cardName,
        action: 'collapse',
        card_expanded: false,
        page_context: 'notification',
        detail_type: 'page',
        ...properties,
      } as NotificationEventProperties);
    },
  };
};
